import React, { useEffect, useMemo, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import posConfig from "./posConfig"; // Adjust path if needed
import BillingPage from "./BillingCounter"; // Adjust path if needed
import RoomServiceDashboard from "./RoomServiceDashboard"; // Adjust path if needed
import RestaurantServiceDashboard from "./RestaurantServiceDashboard"; // Adjust path if needed
import { usePosContext } from "../../context/PosContext"; // Adjust path if needed
import { Spin } from "antd";
import {
  FaConciergeBell,
  FaUtensils,
  FaCashRegister,
  FaExpandAlt,
  FaCompressAlt,
  FaPrint,
} from "react-icons/fa";

// ─── API URL helper ──────────────────────────────────────────────────────────
const getApiUrl = () =>
  (process.env.REACT_APP_API_URL || "http://localhost:4000/api").replace(/\/api\/pos$/, "/api");

// ─── Agent Status Hook ────────────────────────────────────────────────────────
/**
 * Polls GET /api/pos/agent-status every 30 seconds.
 * Returns { online, lastSeen, loading }
 * Used to show green/red pill in POS header.
 */
const useAgentStatus = () => {
  const [status, setStatus] = useState({ online: false, lastSeen: null, loading: true });

  const check = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) { setStatus({ online: false, lastSeen: null, loading: false }); return; }
    try {
      const res = await axios.get(`${getApiUrl()}/pos/agent-status`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000,
      });
      setStatus({ online: res.data?.online ?? false, lastSeen: res.data?.lastSeen ?? null, loading: false });
    } catch {
      setStatus({ online: false, lastSeen: null, loading: false });
    }
  }, []);

  useEffect(() => {
    check();
    const interval = setInterval(check, 30000);
    return () => clearInterval(interval);
  }, [check]);

  return status;
};

// ─── Agent Status Pill ───────────────────────────────────────────────────────
const AgentStatusPill = ({ status }) => {
  const { online, lastSeen, loading } = status;
  if (loading) return (
    <span className="ns-pos-status-pill" title="Checking kitchen printer agent...">
      <span className="ns-pos-agent-dot ns-pos-agent-dot--checking" />
      <FaPrint style={{ fontSize: "0.7rem", opacity: 0.6 }} />
      Checking…
    </span>
  );
  if (online) return (
    <span className="ns-pos-status-pill ns-pos-status-pill--agent-online"
      title="Kitchen print agent is connected — KOTs will print automatically">
      <span className="ns-pos-agent-dot ns-pos-agent-dot--online" />
      <FaPrint style={{ fontSize: "0.7rem", opacity: 0.8 }} />
      Agent Online
    </span>
  );
  const lastSeenText = lastSeen
    ? `Last seen ${new Date(lastSeen).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}`
    : "Never connected";
  return (
    <span className="ns-pos-status-pill ns-pos-status-pill--agent-offline"
      title={`Kitchen print agent offline — KOTs will not print. ${lastSeenText}`}>
      <span className="ns-pos-agent-dot ns-pos-agent-dot--offline" />
      <FaPrint style={{ fontSize: "0.7rem", opacity: 0.8 }} />
      Agent Offline
    </span>
  );
};
const getModeIcon = (modeId) => {
  if (modeId === "room_service") return <FaConciergeBell />;
  if (modeId === "restaurant_billing") return <FaUtensils />;
  if (modeId === "counter_billing") return <FaCashRegister />;
  return <FaCashRegister />;
};

const getModeTone = (modeId) => {
  if (modeId === "room_service") {
    return {
      accent: "#2563eb",
      soft: "rgba(37, 99, 235, 0.10)",
      label: "Room Service",
      helper: "Room-led guest ordering and service control",
    };
  }

  if (modeId === "restaurant_billing") {
    return {
      accent: "#0f766e",
      soft: "rgba(20, 184, 166, 0.12)",
      label: "Restaurant",
      helper: "Restaurant table service and billing workspace",
    };
  }

  if (modeId === "counter_billing") {
    return {
      accent: "#b45309",
      soft: "rgba(245, 158, 11, 0.13)",
      label: "Counter",
      helper: "Fast counter billing and walk-in order control",
    };
  }

  return {
    accent: "#2563eb",
    soft: "rgba(37, 99, 235, 0.10)",
    label: "POS",
    helper: "Point-of-sale workspace",
  };
};

const TableDashboard = () => {
  const { rooms, fetchRooms } = usePosContext();
  const [selectedMode, setSelectedMode] = useState(posConfig.defaultMode);
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingLabel, setLoadingLabel] = useState("Loading POS workspace...");

  // Default POS workspace opens maximized.
  // Click Minimize to restore it to its normal page position.
  const [isMaximized, setIsMaximized] = useState(true);

  // Kitchen agent status — polls every 30s, shows green/red pill in header
  const agentStatus = useAgentStatus();

  const modes = useMemo(
    () => (Array.isArray(posConfig?.modes) ? posConfig.modes : []),
    []
  );

  const selectedModeConfig = useMemo(
    () => modes.find((mode) => mode.id === selectedMode),
    [modes, selectedMode]
  );

  const selectedTone = useMemo(() => getModeTone(selectedMode), [selectedMode]);

  const isCounterMode = useMemo(() => {
    const modeId = String(selectedMode || "").toLowerCase();
    const componentName = String(selectedModeConfig?.component || "").toLowerCase();
    const label = String(selectedModeConfig?.label || "").toLowerCase();

    return (
      modeId === "counter_billing" ||
      modeId === "counter" ||
      modeId === "counterbilling" ||
      componentName === "billingpage" ||
      componentName.includes("counter") ||
      label.includes("counter")
    );
  }, [selectedMode, selectedModeConfig]);

  const roomCount = Array.isArray(rooms) ? rooms.length : 0;

  useEffect(() => {
    console.log("Rooms updated in TableDashboard:", rooms);

    if (selectedMode === "room_service") {
      setRefreshKey((prev) => prev + 1);
      setLoading(false);
    }
  }, [rooms, selectedMode]);

  useEffect(() => {
    if (!isMaximized) {
      document.body.classList.remove("ns-pos-body-locked");
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsMaximized(false);
      }
    };

    document.body.classList.add("ns-pos-body-locked");
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.classList.remove("ns-pos-body-locked");
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isMaximized]);

  useEffect(() => {
    return () => {
      document.body.classList.remove("ns-pos-body-locked");
    };
  }, []);

  const handleModeChange = async (modeId) => {
    const nextMode = modes.find((mode) => mode.id === modeId);

    if (!nextMode) {
      console.warn(`Mode "${modeId}" not found in posConfig.`);
      setSelectedMode(modeId);
      return;
    }

    setSelectedMode(modeId);
    setRefreshKey((prev) => prev + 1);

    const tone = getModeTone(modeId);
    setLoadingLabel(`Opening ${tone.label} workspace...`);

    if (modeId === "room_service" && (!rooms || rooms.length === 0)) {
      console.log("Rooms not loaded, forcing fetch...");
      setLoading(true);

      try {
        if (typeof fetchRooms === "function") {
          await fetchRooms();
        } else {
          console.warn("fetchRooms method not available in PosContext. Please implement it.");
        }
      } catch (error) {
        console.error("Error fetching rooms:", error);
      } finally {
        setLoading(false);
      }

      return;
    }

    setLoading(false);
  };

  const renderModeError = () => (
    <div className="ns-pos-mode-error">
      <div className="ns-pos-mode-error-card">
        <div className="ns-pos-mode-error-icon">!</div>
        <h4>Mode not available</h4>
        <p>
          You are not subscribed to this feature or the selected POS mode is not configured.
        </p>
        <code>Mode: {selectedMode || "not selected"}</code>
      </div>
    </div>
  );

  const renderLoader = () => (
    <div className="ns-pos-workspace-loader">
      <div className="ns-pos-loader-card">
        <Spin size="large" />
        <div>
          <strong>{loadingLabel}</strong>
          <span>Please wait while the workspace is prepared.</span>
        </div>
      </div>
    </div>
  );

  const renderComponent = () => {
    const mode = selectedModeConfig;

    console.log("Selected Mode:", selectedMode);
    console.log("Mode Object:", mode);

    if (!mode) return renderModeError();
    if (loading) return renderLoader();

    switch (mode.component) {
      case "RoomServiceDashboard":
        return <RoomServiceDashboard key={`room-service-${refreshKey}`} />;
      case "BillingPage":
        return <BillingPage key={`counter-billing-${refreshKey}`} />;
      case "RestaurantBillingDashboard":
        return <RestaurantServiceDashboard key={`restaurant-billing-${refreshKey}`} />;
      default:
        return renderModeError();
    }
  };

  const dashboardMarkup = (
    <div
      className={`ns-pos-dashboard-wrapper ${
        isMaximized ? "ns-pos-shell-maximized" : "page-wrapper ns-pos-shell-normal"
      }`}
    >
      <style>{`
        body.ns-pos-body-locked {
          overflow: hidden !important;
        }

        /* POS max dropdown overlay fix */
        body.ns-pos-body-locked .ant-select-dropdown,
        body.ns-pos-body-locked .ant-dropdown,
        body.ns-pos-body-locked .ant-picker-dropdown,
        body.ns-pos-body-locked .ant-popover,
        body.ns-pos-body-locked .swal2-container,
        body.ns-pos-body-locked .ns-bill-select__menu-portal,
        body.ns-pos-body-locked .ns-kot-react-select__menu-portal {
          z-index: 2147483647 !important;
        }

        .ns-pos-shell-normal .ns-pos-mode-hero {
          margin-top: 15px;
        }

        .ns-pos-dashboard-wrapper {
          background:
            radial-gradient(circle at 8% 2%, rgba(37, 99, 235, 0.08), transparent 28%),
            radial-gradient(circle at 90% 0%, rgba(20, 184, 166, 0.10), transparent 26%),
            #f8fafc;
          min-height: calc(100vh - 60px);
        }

        .ns-pos-shell-normal {
          padding: 0;
          margin-top: 0 !important;
        }

        .ns-pos-shell-normal .ns-pos-dashboard-page {
          padding-top: 26px;
          padding-bottom: 18px;
        }

        /*
          Maximized mode is rendered through React Portal into document.body.
          The outer shell covers the full app; scrolling happens inside
          .ns-pos-workspace-body so the top mode bar remains visible.
        */
        .ns-pos-shell-maximized {
          position: fixed !important;
          inset: 0 !important;
          width: 100vw !important;
          height: 100vh !important;
          height: 100dvh !important;
          min-width: 100vw !important;
          min-height: 100vh !important;
          min-height: 100dvh !important;
          margin: 0 !important;
          padding: 0 !important;
          z-index: 2147483646 !important;
          overflow: hidden !important;
          isolation: isolate !important;
          background:
            radial-gradient(circle at 8% 2%, rgba(37, 99, 235, 0.08), transparent 28%),
            radial-gradient(circle at 90% 0%, rgba(20, 184, 166, 0.10), transparent 26%),
            #f8fafc !important;
        }

        .ns-pos-dashboard-page {
          width: 100%;
        }

        .ns-pos-shell-normal .ns-pos-dashboard-page {
          padding-left: 10px;
          padding-right: 10px;
        }

        .ns-pos-shell-maximized .ns-pos-dashboard-page {
          width: 100% !important;
          max-width: none !important;
          height: 100vh !important;
          height: 100dvh !important;
          min-height: 0 !important;
          padding: 8px !important;
          display: flex !important;
          flex-direction: column !important;
          overflow: hidden !important;
        }

        .ns-pos-mode-hero {
          position: sticky;
          top: 0;
          z-index: 30;
          background:
            radial-gradient(circle at top left, ${selectedTone.soft}, transparent 34%),
            linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,251,255,0.98) 100%);
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 24px;
          box-shadow: 0 18px 44px rgba(15, 23, 42, 0.08);
          padding: 14px;
          margin-bottom: 14px;
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
        }

        .ns-pos-shell-maximized .ns-pos-mode-hero {
          position: relative;
          top: auto;
          z-index: 40;
          border-radius: 18px;
          padding: 9px 11px;
          margin-bottom: 8px;
          flex: 0 0 auto;
          box-shadow: 0 12px 30px rgba(15, 23, 42, 0.07);
        }

        .ns-pos-mode-hero-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 14px;
          flex-wrap: wrap;
          margin-bottom: 12px;
        }

        .ns-pos-shell-maximized .ns-pos-mode-hero-top {
          margin-bottom: 8px;
        }

        .ns-pos-mode-title-wrap {
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 0;
        }

        .ns-pos-mode-main-icon {
          width: 46px;
          height: 46px;
          border-radius: 17px;
          background: ${selectedTone.accent};
          color: #ffffff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 14px 28px ${selectedTone.soft};
          flex: 0 0 auto;
          font-size: 1.18rem;
        }

        .ns-pos-shell-maximized .ns-pos-mode-main-icon {
          width: 40px;
          height: 40px;
          border-radius: 14px;
          font-size: 1rem;
        }

        .ns-pos-mode-eyebrow {
          color: ${selectedTone.accent};
          font-size: 0.7rem;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          font-weight: 950;
          margin-bottom: 4px;
        }

        .ns-pos-mode-title {
          margin: 0;
          color: #001f5b;
          font-size: 1.26rem;
          line-height: 1.16;
          font-weight: 950;
        }

        .ns-pos-shell-maximized .ns-pos-mode-title {
          font-size: 1.05rem;
        }

        .ns-pos-mode-subtitle {
          color: #64748b;
          font-size: 0.82rem;
          margin-top: 4px;
          line-height: 1.45;
        }

        .ns-pos-shell-maximized .ns-pos-mode-subtitle {
          font-size: 0.74rem;
          margin-top: 2px;
        }

        .ns-pos-mode-status {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 8px;
          flex-wrap: wrap;
        }

        .ns-pos-status-pill {
          min-height: 34px;
          border-radius: 999px;
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          color: #334155;
          padding: 7px 11px;
          font-size: 0.75rem;
          font-weight: 900;
          display: inline-flex;
          align-items: center;
          gap: 7px;
          box-shadow: 0 8px 18px rgba(15, 23, 42, 0.04);
          white-space: nowrap;
        }

        .ns-pos-shell-maximized .ns-pos-status-pill {
          min-height: 31px;
          padding: 6px 10px;
          font-size: 0.7rem;
        }

        .ns-pos-live-dot {
          width: 8px;
          height: 8px;
          border-radius: 999px;
          background: #22c55e;
          box-shadow: 0 0 0 5px rgba(34, 197, 94, 0.12);
        }

        /* ── Agent status pill variants ───────────────────────────────────── */
        .ns-pos-status-pill--agent-online {
          border-color: #bbf7d0;
          background: linear-gradient(135deg, #f0fdf4, #ecfdf5);
          color: #166534;
        }
        .ns-pos-status-pill--agent-offline {
          border-color: #fecaca;
          background: linear-gradient(135deg, #fff5f5, #fef2f2);
          color: #991b1b;
        }
        .ns-pos-agent-dot {
          width: 8px; height: 8px; border-radius: 999px; flex: 0 0 auto;
        }
        .ns-pos-agent-dot--online {
          background: #22c55e;
          box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.2);
          animation: ns-agent-pulse 2s ease-in-out infinite;
        }
        .ns-pos-agent-dot--offline {
          background: #ef4444;
          box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.15);
        }
        .ns-pos-agent-dot--checking {
          background: #f59e0b;
          box-shadow: 0 0 0 4px rgba(245, 158, 11, 0.15);
          animation: ns-agent-blink 1s ease-in-out infinite;
        }
        @keyframes ns-agent-pulse {
          0%, 100% { box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.2); }
          50%       { box-shadow: 0 0 0 7px rgba(34, 197, 94, 0.08); }
        }
        @keyframes ns-agent-blink {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.4; }
        }

        .ns-pos-window-toggle {
          position: relative;
          z-index: 45;
          min-height: 34px;
          border-radius: 999px;
          border: 1px solid rgba(15, 23, 42, 0.10);
          background: #0f172a;
          color: #ffffff;
          padding: 7px 12px;
          font-size: 0.74rem;
          font-weight: 950;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          cursor: pointer;
          box-shadow: 0 12px 28px rgba(15, 23, 42, 0.16);
          transition: transform 0.16s ease, box-shadow 0.16s ease, background 0.16s ease;
          white-space: nowrap;
        }

        .ns-pos-window-toggle:hover {
          transform: translateY(-1px);
          background: ${selectedTone.accent};
          box-shadow: 0 16px 34px rgba(15, 23, 42, 0.22);
        }


        .ns-pos-hero-center-action {
          flex: 0 0 auto;
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 148px;
          align-self: center;
        }

        .ns-pos-inline-minimize {
          min-height: 34px;
          border-radius: 999px;
          border: 1px solid rgba(15, 23, 42, 0.10);
          background: #0f172a;
          color: #ffffff;
          padding: 0 12px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          font-size: 0.74rem;
          font-weight: 950;
          box-shadow: 0 12px 28px rgba(15, 23, 42, 0.16);
          cursor: pointer;
          transition: transform 0.16s ease, background 0.16s ease, box-shadow 0.16s ease;
          white-space: nowrap;
        }

        .ns-pos-inline-minimize:hover {
          transform: translateY(-1px);
          background: ${selectedTone.accent};
          box-shadow: 0 16px 34px rgba(15, 23, 42, 0.22);
        }


        .ns-pos-inline-minimize.is-maximize {
          background: linear-gradient(135deg, #1d4ed8, #2563eb);
          box-shadow: 0 14px 32px rgba(37, 99, 235, 0.18);
        }

        .ns-pos-inline-minimize.is-minimize {
          background: #0f172a;
        }

        .ns-pos-inline-minimize svg {
          flex: 0 0 auto;
        }

        .ns-pos-inline-minimize small {
          min-height: 20px;
          border-radius: 999px;
          padding: 3px 7px;
          margin-left: 2px;
          color: rgba(255, 255, 255, 0.82);
          background: rgba(255, 255, 255, 0.14);
          font-size: 0.62rem;
          line-height: 1;
          font-weight: 950;
          display: inline-flex;
          align-items: center;
        }

        @media (max-width: 920px) {
          .ns-pos-hero-center-action {
            order: 3;
            width: 100%;
            min-width: 0;
            justify-content: flex-start;
          }
        }

        @media (max-width: 640px) {
          .ns-pos-inline-minimize {
            min-height: 34px;
            padding: 0 11px;
            font-size: 0.72rem;
          }

          .ns-pos-inline-minimize small {
            display: none;
          }
        }

        .ns-pos-mode-switcher {
          display: grid;
          grid-template-columns: repeat(${Math.max(modes.length, 1)}, minmax(0, 1fr));
          gap: 10px;
        }

        .ns-pos-shell-maximized .ns-pos-mode-switcher {
          gap: 8px;
        }

        .ns-pos-mode-card {
          position: relative;
          overflow: hidden;
          min-height: 78px;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 18px;
          background: #ffffff;
          padding: 12px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 11px;
          text-align: left;
          transition:
            transform 0.16s ease,
            box-shadow 0.16s ease,
            border-color 0.16s ease,
            background 0.16s ease;
          box-shadow: 0 8px 22px rgba(15, 23, 42, 0.05);
        }

        .ns-pos-shell-maximized .ns-pos-mode-card {
          min-height: 56px;
          border-radius: 15px;
          padding: 8px 9px;
        }

        .ns-pos-mode-card:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 14px 32px rgba(15, 23, 42, 0.09);
        }

        .ns-pos-mode-card.active {
          border-color: var(--mode-accent);
          background:
            radial-gradient(circle at top right, var(--mode-soft), transparent 38%),
            #ffffff;
          box-shadow: 0 14px 34px rgba(15, 23, 42, 0.10);
        }

        .ns-pos-mode-card.active::after {
          content: "";
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          height: 4px;
          background: var(--mode-accent);
        }

        .ns-pos-mode-card:disabled {
          opacity: 0.72;
          cursor: wait;
        }

        .ns-pos-card-icon {
          width: 42px;
          height: 42px;
          border-radius: 15px;
          color: var(--mode-accent);
          background: var(--mode-soft);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 auto;
          font-size: 1.06rem;
        }

        .ns-pos-shell-maximized .ns-pos-card-icon {
          width: 34px;
          height: 34px;
          border-radius: 12px;
          font-size: 0.9rem;
        }

        .ns-pos-mode-card.active .ns-pos-card-icon {
          background: var(--mode-accent);
          color: #ffffff;
        }

        .ns-pos-card-copy {
          min-width: 0;
        }

        .ns-pos-card-copy strong {
          display: block;
          color: #0f172a;
          font-size: 0.92rem;
          line-height: 1.22;
          font-weight: 950;
          margin-bottom: 3px;
        }

        .ns-pos-shell-maximized .ns-pos-card-copy strong {
          font-size: 0.82rem;
          margin-bottom: 1px;
        }

        .ns-pos-card-copy span {
          display: block;
          color: #64748b;
          font-size: 0.74rem;
          line-height: 1.35;
          font-weight: 700;
        }

        .ns-pos-shell-maximized .ns-pos-card-copy span {
          font-size: 0.67rem;
        }

        .ns-pos-card-badge {
          margin-left: auto;
          border-radius: 999px;
          background: #f8fafc;
          color: #64748b;
          border: 1px solid rgba(15, 23, 42, 0.08);
          padding: 4px 8px;
          font-size: 0.66rem;
          font-weight: 950;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          white-space: nowrap;
        }

        .ns-pos-mode-card.active .ns-pos-card-badge {
          background: #ecfdf5;
          color: #047857;
          border-color: #bbf7d0;
        }


        .ns-pos-shell-normal .ns-pos-workspace {
          overflow: visible;
        }

        .ns-pos-shell-normal .ns-pos-workspace-body {
          min-height: 0;
          overflow: visible;
        }

        .ns-pos-shell-maximized .ns-rest-dashboard-root,
        .ns-pos-shell-maximized .ns-rsd-root {
          padding-top: 0 !important;
        }

        .ns-pos-workspace {
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 24px;
          box-shadow: 0 18px 48px rgba(15, 23, 42, 0.07);
          overflow: hidden;
          min-height: 520px;
        }

        .ns-pos-shell-maximized .ns-pos-workspace {
          flex: 1 1 auto !important;
          min-height: 0 !important;
          height: auto !important;
          border-radius: 18px;
          display: flex !important;
          flex-direction: column !important;
          overflow: hidden !important;
        }

        .ns-pos-workspace-body {
          min-height: 480px;
        }

        /*
          Scroll fix:
          Body is locked in maximized mode so the page behind does not move.
          Therefore the internal workspace body must own vertical scrolling.
        */
        .ns-pos-shell-maximized .ns-pos-workspace-body {
          flex: 1 1 auto !important;
          min-height: 0 !important;
          height: 100% !important;
          overflow-y: auto !important;
          overflow-x: hidden !important;
          overscroll-behavior: contain;
          -webkit-overflow-scrolling: touch;
          padding-bottom: 14px;
          scrollbar-width: thin;
          scrollbar-color: #93c5fd #e2e8f0;
        }

        .ns-pos-shell-maximized .ns-pos-workspace-body::-webkit-scrollbar {
          width: 9px;
        }

        .ns-pos-shell-maximized .ns-pos-workspace-body::-webkit-scrollbar-track {
          background: #e2e8f0;
          border-radius: 999px;
        }

        .ns-pos-shell-maximized .ns-pos-workspace-body::-webkit-scrollbar-thumb {
          background: #93c5fd;
          border-radius: 999px;
        }

        /*
          Let child dashboards become normal scrollable content inside the
          maximized workspace. This prevents their own overflow:hidden rules
          from killing the page-level vertical scroll.
        */
        .ns-pos-shell-maximized .ns-rsd-root,
        .ns-pos-shell-maximized .ns-rest-root {
          min-height: auto !important;
          height: auto !important;
          overflow: visible !important;
        }

        .ns-pos-shell-maximized .ns-rsd-room-panel,
        .ns-pos-shell-maximized .ns-rest-main {
          max-width: none;
        }

        .ns-pos-shell-maximized .ns-rsd-room-grid,
        .ns-pos-shell-maximized .ns-rest-grid-card {
          max-height: none !important;
          overflow: visible !important;
          padding-bottom: 24px !important;
        }

        .ns-pos-workspace-loader {
          min-height: 520px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f8fafc;
        }

        .ns-pos-loader-card {
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 22px;
          padding: 26px 32px;
          display: inline-flex;
          align-items: center;
          gap: 14px;
          box-shadow: 0 24px 60px rgba(15, 23, 42, 0.12);
        }

        .ns-pos-loader-card strong {
          display: block;
          color: #0f172a;
          font-weight: 950;
          margin-bottom: 3px;
        }

        .ns-pos-loader-card span {
          display: block;
          color: #64748b;
          font-size: 0.82rem;
        }

        .ns-pos-mode-error {
          min-height: 520px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f8fafc;
          padding: 24px;
        }

        .ns-pos-mode-error-card {
          max-width: 440px;
          width: 100%;
          background: #ffffff;
          border: 1px solid rgba(220, 38, 38, 0.16);
          border-radius: 24px;
          box-shadow: 0 22px 55px rgba(15, 23, 42, 0.10);
          padding: 24px;
          text-align: center;
        }

        .ns-pos-mode-error-icon {
          width: 52px;
          height: 52px;
          border-radius: 18px;
          background: #fef2f2;
          color: #b91c1c;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 1.3rem;
          font-weight: 950;
          margin-bottom: 12px;
        }

        .ns-pos-mode-error-card h4 {
          color: #0f172a;
          font-weight: 950;
          margin-bottom: 7px;
        }

        .ns-pos-mode-error-card p {
          color: #64748b;
          line-height: 1.55;
          margin-bottom: 12px;
        }

        .ns-pos-mode-error-card code {
          background: #f8fafc;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 10px;
          padding: 6px 9px;
          color: #b91c1c;
          font-size: 0.78rem;
        }


        /*
          Table grid alignment patch:
          - fixed equal-height cards
          - stable table number position
          - no colored corner glow/background
          - KOT/Bill actions stay aligned at bottom
          - child table-card styles are overridden safely without changing action flow
        */
        .ns-pos-dashboard-wrapper .ns-table-grid-wrap {
          width: 100% !important;
          min-width: 0 !important;
        }

        .ns-pos-dashboard-wrapper .ns-table-grid {
          width: 100% !important;
          display: grid !important;
          grid-template-columns: repeat(auto-fill, minmax(166px, 1fr)) !important;
          grid-auto-rows: 202px !important;
          align-items: stretch !important;
          gap: 10px !important;
          padding: 2px 2px 72px !important;
          background: transparent !important;
        }

        .ns-pos-dashboard-wrapper .ns-table-card-shell {
          height: 100% !important;
          min-height: 0 !important;
          min-width: 0 !important;
          display: block !important;
        }

        .ns-pos-dashboard-wrapper .ns-table-card-shell.merged {
          grid-column: span 2 !important;
          height: 100% !important;
        }

        .ns-pos-dashboard-wrapper .ns-table-card-inner {
          position: relative !important;
          height: 100% !important;
          min-height: 0 !important;
          max-height: none !important;
          border-radius: 16px !important;
          background: #ffffff !important;
          border: 1px solid rgba(15, 23, 42, 0.10) !important;
          border-top: 0 !important;
          box-shadow: 0 10px 22px rgba(15, 23, 42, 0.055) !important;
          padding: 11px !important;
          display: grid !important;
          grid-template-rows: 28px 72px 28px 35px !important;
          gap: 7px !important;
          cursor: pointer !important;
          overflow: hidden !important;
          transition: transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease !important;
        }

        .ns-pos-dashboard-wrapper .ns-table-card-inner:hover {
          transform: translateY(-1px) !important;
          border-color: rgba(37, 99, 235, 0.22) !important;
          box-shadow: 0 16px 34px rgba(15, 23, 42, 0.10) !important;
        }

        .ns-pos-dashboard-wrapper .ns-table-card-inner::before {
          content: "" !important;
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          height: 3px !important;
          background: var(--table-status-color, #22c55e) !important;
          border-radius: 16px 16px 0 0 !important;
          pointer-events: none !important;
          z-index: 1 !important;
        }

        .ns-pos-dashboard-wrapper .ns-table-card-inner::after {
          display: none !important;
          content: none !important;
        }

        .ns-pos-dashboard-wrapper .ns-table-card-top {
          height: 28px !important;
          min-height: 28px !important;
          display: flex !important;
          justify-content: space-between !important;
          align-items: center !important;
          gap: 8px !important;
          position: relative !important;
          z-index: 2 !important;
        }

        .ns-pos-dashboard-wrapper .ns-table-status-indicator {
          min-width: 0 !important;
          display: inline-flex !important;
          align-items: center !important;
          gap: 7px !important;
          color: #0f172a !important;
          font-size: 0.72rem !important;
          font-weight: 950 !important;
          line-height: 1 !important;
          overflow: hidden !important;
        }

        .ns-pos-dashboard-wrapper .ns-table-status-dot {
          width: 11px !important;
          height: 11px !important;
          border-radius: 999px !important;
          background: var(--table-status-color, #22c55e) !important;
          box-shadow: 0 0 0 5px color-mix(in srgb, var(--table-status-color, #22c55e) 13%, transparent) !important;
          flex: 0 0 auto !important;
        }

        .ns-pos-dashboard-wrapper .ns-table-status-text {
          display: inline-block !important;
          max-width: 74px !important;
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          white-space: nowrap !important;
        }

        .ns-pos-dashboard-wrapper .ns-table-kot-badge {
          height: 27px !important;
          min-height: 27px !important;
          max-height: 27px !important;
          border-radius: 999px !important;
          background: #f8fafc !important;
          color: #475569 !important;
          border: 1px solid rgba(15, 23, 42, 0.08) !important;
          padding: 0 10px !important;
          font-size: 0.68rem !important;
          font-weight: 950 !important;
          white-space: nowrap !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          flex: 0 0 auto !important;
        }

        .ns-pos-dashboard-wrapper .ns-table-kot-badge:not(.muted) {
          background: #eff6ff !important;
          color: #1d4ed8 !important;
          border-color: #bfdbfe !important;
        }

        .ns-pos-dashboard-wrapper .ns-table-card-focus {
          height: 72px !important;
          min-height: 72px !important;
          max-height: 72px !important;
          overflow: hidden !important;
          display: block !important;
          position: relative !important;
          z-index: 2 !important;
        }

        .ns-pos-dashboard-wrapper .ns-table-tooltip-trigger {
          width: 100% !important;
          height: 72px !important;
          min-height: 72px !important;
          display: grid !important;
          grid-template-rows: 34px 22px !important;
          align-content: start !important;
          align-items: start !important;
          justify-items: start !important;
          gap: 6px !important;
          text-align: left !important;
          padding-top: 7px !important;
          overflow: hidden !important;
        }

        .ns-pos-dashboard-wrapper .ns-table-title-row {
          width: 100% !important;
          height: 34px !important;
          min-height: 34px !important;
          display: flex !important;
          align-items: flex-start !important;
          margin: 0 !important;
          overflow: hidden !important;
        }

        .ns-pos-dashboard-wrapper .ns-table-name {
          display: block !important;
          width: 100% !important;
          max-width: 100% !important;
          color: #001f5b !important;
          font-size: 1.18rem !important;
          font-weight: 950 !important;
          line-height: 1.12 !important;
          margin: 0 !important;
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          white-space: nowrap !important;
        }

        .ns-pos-dashboard-wrapper .ns-table-zone {
          width: 100% !important;
          height: 22px !important;
          min-height: 22px !important;
          max-height: 22px !important;
          color: #64748b !important;
          font-size: 0.72rem !important;
          font-weight: 850 !important;
          display: inline-flex !important;
          align-items: center !important;
          gap: 5px !important;
          overflow: hidden !important;
          margin: 0 !important;
        }

        .ns-pos-dashboard-wrapper .ns-table-zone span {
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          white-space: nowrap !important;
        }

        .ns-pos-dashboard-wrapper .ns-table-mini-stats,
        .ns-pos-dashboard-wrapper .ns-table-capacity-dots,
        .ns-pos-dashboard-wrapper .ns-table-since,
        .ns-pos-dashboard-wrapper .ns-table-time-chip,
        .ns-pos-dashboard-wrapper .ns-table-merged-title {
          display: none !important;
        }

        .ns-pos-dashboard-wrapper .ns-table-metrics,
        .ns-pos-dashboard-wrapper .ns-table-merged-meta {
          height: 28px !important;
          min-height: 28px !important;
          max-height: 28px !important;
          display: flex !important;
          align-items: center !important;
          flex-wrap: nowrap !important;
          gap: 5px !important;
          overflow: hidden !important;
          position: relative !important;
          z-index: 2 !important;
        }

        .ns-pos-dashboard-wrapper .ns-table-metrics span,
        .ns-pos-dashboard-wrapper .ns-table-merged-meta span {
          height: 26px !important;
          min-height: 26px !important;
          border-radius: 999px !important;
          background: #ffffff !important;
          border: 1px solid rgba(15, 23, 42, 0.08) !important;
          color: #475569 !important;
          padding: 0 7px !important;
          font-size: 0.67rem !important;
          font-weight: 900 !important;
          display: inline-flex !important;
          align-items: center !important;
          white-space: nowrap !important;
          flex: 0 0 auto !important;
        }

        .ns-pos-dashboard-wrapper .ns-table-actions {
          height: 35px !important;
          min-height: 35px !important;
          max-height: 35px !important;
          margin-top: 0 !important;
          display: flex !important;
          justify-content: flex-start !important;
          align-items: center !important;
          gap: 7px !important;
          flex-wrap: nowrap !important;
          overflow: hidden !important;
          position: relative !important;
          z-index: 2 !important;
        }

        .ns-pos-dashboard-wrapper .ns-table-action-btn {
          min-height: 32px !important;
          height: 32px !important;
          border-radius: 12px !important;
          border: 0 !important;
          padding: 0 10px !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 6px !important;
          font-size: 0.72rem !important;
          font-weight: 950 !important;
          white-space: nowrap !important;
          flex: 0 0 auto !important;
        }

        .ns-pos-dashboard-wrapper .ns-table-card-inner.merged-card {
          grid-template-rows: 28px 72px 28px 35px !important;
        }

        @supports not (background: color-mix(in srgb, red 10%, transparent)) {
          .ns-pos-dashboard-wrapper .ns-table-status-dot {
            box-shadow: 0 0 0 5px rgba(37, 99, 235, 0.11) !important;
          }
        }

        @media (max-width: 1280px) {
          .ns-pos-dashboard-wrapper .ns-table-grid {
            grid-template-columns: repeat(auto-fill, minmax(158px, 1fr)) !important;
            grid-auto-rows: 202px !important;
          }
        }

        @media (max-width: 760px) {
          .ns-pos-dashboard-wrapper .ns-table-grid {
            grid-template-columns: repeat(auto-fill, minmax(148px, 1fr)) !important;
            grid-auto-rows: 196px !important;
          }

          .ns-pos-dashboard-wrapper .ns-table-card-shell.merged {
            grid-column: span 1 !important;
          }

          .ns-pos-dashboard-wrapper .ns-table-name {
            font-size: 1.06rem !important;
          }

          .ns-pos-dashboard-wrapper .ns-table-action-btn span {
            display: none !important;
          }

          .ns-pos-dashboard-wrapper .ns-table-action-btn {
            width: 33px !important;
            padding: 0 !important;
          }
        }

        @media (max-width: 1100px) {
          .ns-pos-mode-switcher {
            grid-template-columns: 1fr;
          }

          .ns-pos-mode-hero {
            position: relative;
            top: auto;
          }
        }

        @media (max-width: 768px) {
          .ns-pos-dashboard-page {
            padding-left: 10px;
            padding-right: 10px;
          }

          .ns-pos-shell-maximized .ns-pos-dashboard-page {
            padding: 6px !important;
          }

          .ns-pos-mode-hero {
            padding: 12px;
            border-radius: 20px;
          }

          .ns-pos-mode-title {
            font-size: 1.05rem;
          }

          .ns-pos-mode-main-icon {
            width: 42px;
            height: 42px;
            border-radius: 15px;
          }

          .ns-pos-mode-status {
            justify-content: flex-start;
          }

          .ns-pos-card-badge {
            display: none;
          }

  
        .ns-pos-shell-normal .ns-pos-workspace {
          overflow: visible;
        }

        .ns-pos-shell-normal .ns-pos-workspace-body {
          min-height: 0;
          overflow: visible;
        }

        .ns-pos-shell-maximized .ns-rest-dashboard-root,
        .ns-pos-shell-maximized .ns-rsd-root {
          padding-top: 0 !important;
        }

        .ns-pos-workspace {
            border-radius: 20px;
          }
        }
      `}</style>

      <div className="content container-fluid ns-pos-dashboard-page">
        <section className="ns-pos-mode-hero">
          <div className="ns-pos-mode-hero-top">
            <div className="ns-pos-mode-title-wrap">
              <span className="ns-pos-mode-main-icon">
                {getModeIcon(selectedMode)}
              </span>

              <div>
                <div className="ns-pos-mode-eyebrow">POS Workspace</div>
                <h2 className="ns-pos-mode-title">
                  {selectedModeConfig ? selectedModeConfig.label : selectedTone.label}
                </h2>
                <div className="ns-pos-mode-subtitle">
                  {selectedTone.helper}
                </div>
              </div>
            </div>
            {isCounterMode && (
              <div className="ns-pos-hero-center-action">
                <button
                  type="button"
                  className={`ns-pos-inline-minimize ${isMaximized ? "is-minimize" : "is-maximize"}`}
                  onClick={() => setIsMaximized((prev) => !prev)}
                  title={isMaximized ? "Minimize Counter Billing workspace" : "Maximize Counter Billing workspace"}
                  aria-label={isMaximized ? "Minimize Counter Billing workspace" : "Maximize Counter Billing workspace"}
                >
                  {isMaximized ? <FaCompressAlt /> : <FaExpandAlt />}
                  <span>{isMaximized ? "Minimize" : "Maximize"}</span>
                  {isMaximized ? <small>Esc</small> : null}
                </button>
              </div>
            )}

            <div className="ns-pos-mode-status">
              {/* Kitchen agent status — green when connected, red when offline */}
              <AgentStatusPill status={agentStatus} />
              <span className="ns-pos-status-pill">
                {roomCount} room{roomCount === 1 ? "" : "s"} loaded
              </span>
              <span className="ns-pos-status-pill">
                {modes.length} mode{modes.length === 1 ? "" : "s"} available
              </span>

              <button
                type="button"
                className="ns-pos-window-toggle"
                onClick={() => setIsMaximized((prev) => !prev)}
                title={isMaximized ? "Minimize POS workspace" : "Maximize POS workspace"}
                aria-label={isMaximized ? "Minimize POS workspace" : "Maximize POS workspace"}
              >
                {isMaximized ? <FaCompressAlt /> : <FaExpandAlt />}
                <span>{isMaximized ? "Minimize" : "Maximize"}</span>
              </button>
            </div>
          </div>

          <div className="ns-pos-mode-switcher" role="tablist" aria-label="POS modes">
            {modes.map((mode) => {
              const tone = getModeTone(mode.id);
              const isActive = selectedMode === mode.id;

              return (
                <button
                  key={mode.id}
                  type="button"
                  onClick={() => handleModeChange(mode.id)}
                  disabled={loading && isActive}
                  className={`ns-pos-mode-card ${isActive ? "active" : ""}`}
                  style={{
                    "--mode-accent": tone.accent,
                    "--mode-soft": tone.soft,
                  }}
                  role="tab"
                  aria-selected={isActive}
                >
                  <span className="ns-pos-card-icon">
                    {getModeIcon(mode.id)}
                  </span>

                  <span className="ns-pos-card-copy">
                    <strong>{mode.label}</strong>
                    <span>{tone.helper}</span>
                  </span>

                  <span className="ns-pos-card-badge">
                    {isActive ? "Active" : "Open"}
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        <section className="ns-pos-workspace">
          <div className="ns-pos-workspace-body">{renderComponent()}</div>
        </section>
      </div>
    </div>
  );

  if (isMaximized && typeof document !== "undefined") {
    return createPortal(dashboardMarkup, document.body);
  }

  return dashboardMarkup;
};

export default TableDashboard;